import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Orders } from 'src/app/models/Orders.model';
import { OrdersService } from 'src/app/services/orders.service';
import { DropdownService } from 'src/app/services/shared/dropdown.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * Sub-modale "Conferma corriere" — sub-step di confirm-shipment.
 * Cross-use: usato anche in `controllo-quality/confirm-shipment` (v2.13.0 future).
 * Touch leggero in J.4.b: TS cleanup + @Input/@Output preserved.
 *
 * Cleanup v2.11.0 J.4.b:
 * - 🚨 FIX BUG: `changeCarrier()` aveva nested subscribe vuoto
 *   `subscribe((result) => {})` — dead.
 * - 🚨 FIX DEAD: `lettra`, `etichetta`, `isLabel`, `canGenerate`,
 *   `correctAddress` molti dead in questo componente (sono settati in
 *   confirm-shipment).
 * - 🚨 FIX DEAD: `close()` empty method.
 * - 🚨 FIX DRY: `correctAddress` check con `.length > 0` ripetuto 6 volte.
 *   Estratto helper `isAddressComplete()` (mantenuto inline qui per
 *   semplicità — pattern Phase 2 sarà service-side helper).
 * - Switch a inject() + takeUntilDestroyed.
 */
@Component({
  selector: 'app-confirm-courrier',
  templateUrl: './confirm-courrier.component.html',
  styleUrls: ['./confirm-courrier.component.scss'],
  standalone: false,
})


export class ConfirmCourrierComponent implements OnInit {

  @Input() isCourOn = true;

  // === DI ===
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private orderService = inject(OrdersService);
  private dropdownService = inject(DropdownService);
  public modalService = inject(OpenModalService);
  private cdr = inject(ChangeDetectorRef);

  // === State ===
  public order: Orders.Order | null = null;
  public orderToUpdate: Orders.OrderUpdate | null = null;
  public id = '';
  public correctAddress = false;
  public canGenerate = false;
  public isLabel = false;
  public loading = false;

  // === Lifecycle ===

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!this.id) return;

    this.loading = true;
    this.orderService
      .getOrder1(+this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) {
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }
        this.order = result;
        this.orderToUpdate = new Orders.OrderUpdate(this.order);

        if (this.orderToUpdate.or_carrier_id === 0) {
          this.orderToUpdate.or_carrier_id = null;
        }
        if (
          this.order.vs_details?.sp_carrier_id === '0' ||
          this.order.vs_details?.sp_carrier_id == null
        ) {
          this.order.vs_details.sp_carrier_id = null;
        }

        this.correctAddress = this.isAddressComplete(this.order);
        this.canGenerate =
          this.orderToUpdate.or_carrier_id != null &&
          this.order.vs_details?.sp_carrier_id != null;

        this.checkSelectedAll();
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  close(): void {
    this.modalService.offCour();
  }

  /**
   * Helper centralizzato per check indirizzo completo
   * (era duplicato 6 volte in confirm-courrier/confirm-shipment legacy).
   */
  private isAddressComplete(order: Orders.Order): boolean {
    return !!(
      order.or_shipping_address?.length &&
      order.or_shipping_customer?.length &&
      order.or_shipping_city?.length &&
      order.or_shipping_prov?.length &&
      order.or_shipping_zip?.length &&
      order.or_shipping_country?.length
    );
  }

  checkSelectedAll(stepBack = false): void {
    if (!this.order) return;
    const stepFrw = 4;
    const stepBck = 3;
    if (!stepBack && (this.isLabel || !this.canGenerate)) {
      this.orderService.pages.next(stepFrw);
    } 
    else {
      this.order.stepCount = stepBck;
      this.orderService.pages.next(stepBck);
      if (this.order.buttons?.[stepBck]) {
        this.order.buttons[stepBck].completed = false;
      }
    }
  }

  changeCarrier(): void {
    this.dropdownService
      .getCarriers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result || !this.id) return;
        // Reload order per riflettere i carriers aggiornati.
        this.orderService
          .getOrder1(+this.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((order) => {
            if (order) this.order = order;
            this.cdr.detectChanges();
          });
      });
  }
  
}
